import React from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useEconomicCircleData } from "./hooks/useEconomicCircleData";
import useCurrency from "../../../../hooks/useCurrency";
import localeText from "../../../../locale";
import Loading from "../../../../components/Loading/Loading";
import { sortByDateDesc } from "./utils/sortByDateDesc";
import EconomicCirclePageHeader from "./components/EconomicCirclePageHeader/EconomicCirclePageHeader";
import EconomicCircleStatusCard from "./components/EconomicCircleStatusCard/EconomicCircleStatusCard";
import EconomicCircleRankingCard from "./components/EconomicCircleRankingCard/EconomicCircleRankingCard";
import EconomicCircleEarningsCard from "./components/EconomicCircleEarningsCard/EconomicCircleEarningsCard";
import EconomicCircleNetworkHighlights from "./components/EconomicCircleNetworkHighlights/EconomicCircleNetworkHighlights";
import EconomicCircleRecentMembersCard from "./components/EconomicCircleRecentMembersCard/EconomicCircleRecentMembersCard";
import EconomicCircleRecentTransactionsCard from "./components/EconomicCircleRecentTransactionsCard/EconomicCircleRecentTransactionsCard";
import "./EconomicCirclePage.css";
import { normalizeExtracoinUsagePercent } from "../../utils/extracoinUsagePercent";

const EconomicCirclePage = ({ userInfo, userTypeInfo, currency: currencyProp }) => {
  const { data, isLoading, error } = useEconomicCircleData({ fetchTransactions: true });
  const { currencySymbol: currency } = useCurrency(
    currencyProp || data?.balance?.currency || data?.rules?.currency,
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("managerDashboardErrorLoad") || "Erro ao carregar dados."}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const directs = data.directs || [];
  const all = data.all || [];
  const directCount = directs.length;
  const totalMembers = all.length;
  const activeInfluencePercent =
    totalMembers > 0 ? Math.round((directCount / totalMembers) * 100) : 0;
  const levelBadge =
    (data.rules && (data.rules.levelName || data.rules.plan || data.rules.role)) || "PARTNER";
  const networkPower = data.balance?.balance ?? data.balance?.total ?? 0;
  const extracoinPercent = normalizeExtracoinUsagePercent(
    data.extracoinUsage ?? data.rules?.extracoinPercentage ?? data.rules?.percentage,
  );
  const recentMembers = sortByDateDesc(all, "date").slice(0, 5);
  const recentTransactions = data.recentTransactions || [];
  const directCommission = data.balance?.directCommission ?? data.balance?.direct ?? 0;
  const networkCommission = data.balance?.networkCommission ?? data.balance?.network ?? 0;
  const totalReceived = data.balance?.totalReceived ?? data.balance?.balance ?? 0;
  const totalAccumulated =
    data.balance?.totalAccumulated ?? data.balance?.lifetime ?? totalReceived;
  const networkEarnings = Number(totalReceived) || 0;
  const monthlyChange = 12;

  return (
    <DashboardLayout>
      <EconomicCirclePageHeader />

      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <EconomicCircleStatusCard
            levelBadge={levelBadge}
            totalMembers={totalMembers}
            networkPower={networkPower}
            activeInfluencePercent={activeInfluencePercent}
            extracoinPercent={extracoinPercent}
            userTypeInfo={userTypeInfo}
          />
        </div>
        {/* <div className="col-12 md:col-4">
          <EconomicCircleRankingCard />
        </div> */}
        <div className="col-12 md:col-6">
          <EconomicCircleEarningsCard
            currency={currency}
            networkEarnings={networkEarnings}
            monthlyChange={monthlyChange}
            totalAccumulated={totalAccumulated}
            directCommission={directCommission}
            networkCommission={networkCommission}
          />
        </div>
      </div>

      <EconomicCircleNetworkHighlights members={recentMembers} />

      <div className="grid">
        <div className="col-12 lg:col-6">
          <EconomicCircleRecentMembersCard members={recentMembers} />
        </div>
        <div className="col-12 lg:col-6">
          <EconomicCircleRecentTransactionsCard
            transactions={recentTransactions}
            currency={currency}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EconomicCirclePage;
